import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Twee schuifknoppen op één balk, bovenop Horizon's prijsfilter.
 *
 * De min- en max-velden van `price-facet-component` blijven de bron van waarheid:
 * dit component schrijft er alleen in en laat het `change`-event doorborrelen,
 * zodat `price-facet-component.updatePriceFilterAndResults()` het filter net zo
 * toepast als wanneer iemand de bedragen intypt. Zonder JavaScript blijven de
 * velden dus gewoon werken; de knoppen hebben geen `name` en sturen niets mee.
 *
 * De knoppen rekenen in minor units (centen), net als Shopify's prijsfilter, en
 * `formatMoney` maakt daar het bedrag van dat in het veld hoort. Zo klopt het
 * ook in munteenheden zonder decimalen.
 *
 * @typedef {Object} PriceRangeSliderComponentRefs
 * @property {HTMLInputElement} minRange - De schuifknop voor het minimum
 * @property {HTMLInputElement} maxRange - De schuifknop voor het maximum
 *
 * @extends {Component<PriceRangeSliderComponentRefs>}
 */
class PriceRangeSliderComponent extends Component {
  requiredRefs = ['minRange', 'maxRange'];

  connectedCallback() {
    super.connectedCallback();
    this.#paint();
  }

  updatedCallback() {
    super.updatedCallback();

    // Een input houdt na gebruik zijn eigen waarde vast, ook als de server een
    // ander `value`-attribuut terugstuurt. Na een re-render (bijvoorbeeld na
    // "Wis alle filters") zetten we de knoppen daarom terug op het attribuut.
    for (const input of [this.refs.minRange, this.refs.maxRange]) {
      const value = input?.getAttribute('value');
      if (input && value != null) input.value = value;
    }

    this.#paint();
  }

  /**
   * Verwerkt het slepen: knoppen mogen elkaar niet voorbij, de velden volgen en
   * de balk wordt opnieuw getekend. Het toepassen van het filter gebeurt pas op
   * `change`, dat naar `price-facet-component` doorborrelt.
   *
   * @param {Object} _data - Ongebruikt
   * @param {Event} event - Het input-event van een van de twee knoppen
   */
  updateFromSlider(_data, event) {
    const { minRange, maxRange } = this.refs;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    let min = Number(minRange.value);
    let max = Number(maxRange.value);

    if (min > max) {
      if (target === minRange) {
        min = max;
        minRange.value = String(min);
      } else {
        max = min;
        maxRange.value = String(max);
      }
    }

    this.#writeToInputs(min, max);
    this.#paint();
  }

  /**
   * Zet de bedragen in de velden van het prijsfilter. Een uiterste stand laat
   * het veld leeg, zodat die kant niet in de URL terechtkomt.
   *
   * @param {number} min - Ondergrens in minor units
   * @param {number} max - Bovengrens in minor units
   */
  #writeToInputs(min, max) {
    const facet = this.closest('price-facet-component');
    if (!(facet instanceof HTMLElement)) return;

    const minInput = facet.querySelector('input[ref="minInput"]');
    const maxInput = facet.querySelector('input[ref="maxInput"]');
    const currency = facet.dataset.currency ?? 'USD';
    const format = (facet.dataset.moneyFormat ?? '{{amount}}').match(/{{\s*\w+\s*}}/)?.[0] ?? '{{amount}}';
    const lowest = Number(this.refs.minRange.min);
    const highest = Number(this.refs.maxRange.max);

    if (minInput instanceof HTMLInputElement) {
      minInput.value = min <= lowest ? '' : formatMoney(min, format, currency);
    }

    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = max >= highest ? '' : formatMoney(max, format, currency);
    }
  }

  /** Tekent het gevulde deel van de balk via twee custom properties. */
  #paint() {
    const { minRange, maxRange } = this.refs;
    if (!minRange || !maxRange) return;

    const highest = Number(maxRange.max);
    if (!highest) return;

    this.style.setProperty('--price-range-min-percent', `${(Number(minRange.value) / highest) * 100}%`);
    this.style.setProperty('--price-range-max-percent', `${(Number(maxRange.value) / highest) * 100}%`);
  }
}

if (!customElements.get('price-range-slider-component')) {
  customElements.define('price-range-slider-component', PriceRangeSliderComponent);
}
